import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2, RotateCcw } from 'lucide-react';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { migrationV1V2Api, type FieldValueJobsResponse } from '../api/migration-v1-v2';
import type { MigrationStepExecution } from '../types';

/**
 * Sous-ligne d'un lot Utilisateurs : avancement de l'import ASYNCHRONE des
 * valeurs de champs (perso/natifs) côté V2.
 *
 * Pendant le lot, les valeurs sont soumises en jobs batch V2 (202 + jobId,
 * traitement par le worker `field_value_imports_queue`) : le lot peut donc
 * être « Migré » alors que les champs s'écrivent encore en arrière-plan.
 * Cette ligne suit cette traîne : compteur, barre de progression, et détail
 * des utilisateurs en échec (rattachés aux records par sourceId).
 *
 * Le GET back rafraîchit lui-même les jobs non terminés auprès de V2 : le
 * composant se contente de poller tant que des items sont en attente (ou que
 * le lot tourne encore, pour capter les jobs des tranches suivantes). Rien
 * n'est rendu tant qu'aucun job n'existe pour ce lot (écoles sans champs
 * perso, lot jamais lancé, kill-switch back).
 */

const POLL_MS = 8000;

interface FieldValueJobsRowProps {
  step: MigrationStepExecution;
  /** Le lot est en train de tourner (chunks en cours) → on continue à poller. */
  stepIsRunning: boolean;
  /** Droit de relancer les échecs (même condition que lancer le lot). */
  canRetry: boolean;
}

export function FieldValueJobsRow({ step, stepIsRunning, canRetry }: FieldValueJobsRowProps) {
  const [data, setData] = useState<FieldValueJobsResponse | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // setData en callback de promesse (jamais en synchrone dans l'effet).
  // Erreur silencieuse : on garde le dernier état connu, le polling réessaiera.
  const fetchJobs = useCallback(() => {
    migrationV1V2Api
      .getFieldValueJobs(step.id)
      .then(setData)
      .catch(() => {});
  }, [step.id]);

  // Fetch au montage + à chaque changement de statut du lot (relance, fin de
  // run) pour capter immédiatement les nouveaux jobs.
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs, step.status]);

  // Polling tant que le lot tourne (des jobs peuvent encore être soumis) ou
  // que des items sont en cours côté V2. L'effet se réarme quand le statut
  // agrégé change, donc le polling s'arrête tout seul une fois terminé.
  useEffect(() => {
    const shouldPoll = stepIsRunning || data?.aggregate.status === 'in_progress';
    if (!shouldPoll) return;
    const timer = setInterval(fetchJobs, POLL_MS);
    return () => clearInterval(timer);
  }, [fetchJobs, stepIsRunning, data?.aggregate.status]);

  if (!data || data.aggregate.status === 'idle') return null;

  const { aggregate, jobs } = data;
  const done = aggregate.successCount + aggregate.failedCount;
  const pct = aggregate.totalSubmitted > 0 ? Math.round((done / aggregate.totalSubmitted) * 100) : 0;
  const running = aggregate.status === 'in_progress' || retrying;
  const hasFailures = aggregate.failedCount > 0;
  const trackingError = jobs.find((j) => j.trackingError)?.trackingError ?? null;

  // Échecs encore d'actualité (dédupliqués par utilisateur côté back).
  const failedItems = aggregate.failedItems ?? [];

  const retryFailures = () => {
    if (retrying) return;
    setRetrying(true);
    migrationV1V2Api
      .retryFieldValueJobs(step.id)
      .catch(() => {})
      .finally(() => {
        setRetrying(false);
        fetchJobs();
      });
  };

  return (
    <>
      <TableRow className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 hover:bg-gray-50/60 dark:hover:bg-slate-800/40">
        <TableCell className="py-0" colSpan={2}>
          <div className="flex items-stretch">
            <div className="ml-5 mr-4 w-px shrink-0 bg-gray-200 dark:bg-slate-700" />
            <div className="min-w-0 flex items-center gap-1.5 py-1 pl-4">
              {running ? (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-violet-500" />
              ) : hasFailures ? (
                <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />
              ) : (
                <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />
              )}
              {/* text-gray-600 (pas 500) : themes.css force `span.text-gray-500`
                  en uppercase+bold hors @layer, ce qui ferait ressembler la
                  sous-ligne à un header de groupe. */}
              <span className="text-xs text-gray-600 dark:text-slate-400 truncate">
                Champs personnalisés
                <span className="text-gray-400 dark:text-slate-500"> — import V2 en arrière-plan</span>
              </span>
            </div>
          </div>
        </TableCell>
        <TableCell className="py-0">
          <div className="flex items-center gap-2 w-full py-1">
            <div className="h-1 flex-1 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  hasFailures ? 'bg-amber-400' : running ? 'bg-violet-400' : 'bg-emerald-400'
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span
              className="shrink-0 text-[11px] tabular-nums text-gray-600 dark:text-slate-400"
              title="Utilisateurs du lot ayant au moins une valeur de champ (perso ou natif) soumise à V2. Un utilisateur sans aucune valeur, ou dont le record a échoué avant la phase champs, n'y figure pas — ce total peut donc être inférieur au nombre de lignes du lot."
            >
              {done}/{aggregate.totalSubmitted} utilisateurs
              {hasFailures && (
                <span className="text-amber-600 dark:text-amber-400"> · {aggregate.failedCount} échec{aggregate.failedCount > 1 ? 's' : ''}</span>
              )}
            </span>
          </div>
        </TableCell>
        <TableCell className="py-0 text-center">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
              running
                ? 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800'
                : hasFailures
                  ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800'
            )}
          >
            {running ? 'En cours' : hasFailures ? 'Échecs partiels' : 'Terminé'}
          </span>
        </TableCell>
        <TableCell className="py-0 text-center">
          <div className="inline-flex items-center gap-2">
            {hasFailures && canRetry && !stepIsRunning && (
              <button
                type="button"
                onClick={retryFailures}
                disabled={retrying}
                className="inline-flex items-center gap-1 rounded border border-amber-200 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-900/20"
              >
                {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                Relancer les échecs
              </button>
            )}
            {(hasFailures || trackingError) && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-0.5 text-[11px] text-gray-600 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              >
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Détail
              </button>
            )}
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 hover:bg-gray-50/60 dark:hover:bg-slate-800/40">
          <TableCell className="py-2" colSpan={5}>
            <div className="ml-10 mr-4 space-y-1.5">
              {trackingError && (
                <p className="text-[11px] text-red-600 dark:text-red-400">
                  Suivi V2 indisponible : {trackingError}
                </p>
              )}
              {failedItems.length > 0 && (
                <ul className="space-y-1 max-h-56 overflow-y-auto pr-2">
                  {failedItems.map((item, i) => (
                    <li key={`${item.userRoleId}-${i}`} className="text-[11px] text-gray-600 dark:text-slate-300">
                      <span className="font-medium">
                        {item.sourceId ? `Ligne V1 #${item.sourceId}` : item.userRoleId}
                      </span>
                      {' — '}
                      {item.errors.length > 0
                        ? item.errors.map((e) => e.message).join(' ; ')
                        : 'Erreur inconnue'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
